<template>
    <InfoBaseForm v-model="formData" :is-loading="isLoading" @save="save()" @cancel="emit('cancel')">
        <template v-slot:top-block>
            <div class="font-italic text-medium-emphasis pb-2">
                {{ $t('automatically_name') }}
            </div>
        </template>
        <template v-slot:center-block>
            <v-row dense>
                <v-col cols="12" md="4">
                    <v-select
                        v-model="formData.categoryId"
                        :items="useCatalogue"
                        item-value="id"
                        item-title="name"
                        :label="`${$t('excursion_code')}*`"
                        density="compact"
                        variant="outlined"
                        color="primary"
                        :rules="[requiredRule('excursion_code')]"
                        :disabled="!!editExcursionServiceId"
                        data-cy="category-id"
                    />
                </v-col>
            </v-row>
            <v-row dense>
                <v-col cols="12" md="4">
                    <v-text-field
                        v-model="formData.startTime"
                        :label="`${$t('start_time')} (HH:mm)`"
                        :rules="[timeFormatRule()]"
                        variant="outlined"
                        color="primary"
                        density="compact"
                        data-cy="start-time"
                        maxlength="5"
                        class="mb-3"
                        hide-details
                        @update:model-value="e => formatTime(e, 'startTime')"
                    />
                </v-col>
            </v-row>
            <v-row dense>
                <v-col cols="12" md="4">
                    <div class="text-subtitle-1 mt-3">{{ $t('escort_language') }}:</div>
                </v-col>
                <v-col cols="12" md="4">
                    <div class="text-subtitle-1 mt-3">{{ $t('type') }}:</div>
                </v-col>
                <v-col cols="12" md="4">
                    <v-btn
                        size="small"
                        icon="mdi-plus"
                        variant="text"
                        color="primary"
                        data-cy="add-row"
                        @click="addRow"
                    />
                </v-col>
            </v-row>
            <v-row v-for="service in formData.accompanimentInfos" :key="service.id" dense>
                <v-col cols="12" md="4">
                    <v-autocomplete
                        v-model="service.languageCode"
                        :items="useLanguages"
                        density="compact"
                        variant="outlined"
                        color="primary"
                        data-cy="language"
                    />
                </v-col>
                <v-col cols="12" md="4">
                    <v-select
                        v-model="service.type"
                        :items="useExcursionService"
                        density="compact"
                        variant="outlined"
                        color="primary"
                        data-cy="type"
                    />
                </v-col>
                <v-col cols="12" md="4">
                    <v-btn
                        size="small"
                        icon="mdi-close"
                        variant="text"
                        color="error"
                        class="ml-auto"
                        :data-cy="`remove-${service.id}`"
                        @click="removeRow(service.id)"
                    />
                </v-col>
            </v-row>
        </template>
        <template v-slot:bottom-block>
            <div class="text-subtitle-1">{{ $t('number_of_tourists') }}:</div>
            <v-row dense>
                <v-col class="flex-grow-0">
                    <v-text-field
                        v-model="formData.minOccupancy"
                        type="number"
                        hide-spin-buttons
                        variant="outlined"
                        color="primary"
                        density="compact"
                        data-cy="min-occupancy"
                        width="100px"
                    />
                </v-col>
                <v-col class="flex-grow-0">
                    <v-text-field
                        v-model="formData.maxOccupancy"
                        type="number"
                        hide-spin-buttons
                        variant="outlined"
                        color="primary"
                        density="compact"
                        data-cy="max-occupancy"
                        width="100px"
                    />
                </v-col>
            </v-row>
        </template>
    </InfoBaseForm>
</template>

<script setup lang="ts">
    import InfoBaseForm from '~src/components/products/information/InfoBaseForm.vue'
    import {initExcursionServiceFormData} from '~/utils/types/account/products/helpers'
    import {requiredRule, timeFormatRule} from '~/composables/rules'
    import {nanoid} from 'nanoid'
    import {ExcursionService, Sharing, Transportation} from '~/utils/types/types'
    import {useCatalogue} from '~/composables/form-helpers'

    const props = defineProps<{
        editExcursionServiceId?: number | null
        contractId: number
    }>()

    const isLoading = ref<boolean>(false)
    const iso2Codes = ref<string[]>([])
    const formData = ref<any>(initExcursionServiceFormData())
    const contract = ref({
        transportationType: Transportation.Walking,
        sharingType: Sharing.Shared,
        productVariant: {variantName: ''},
    })

    const emit = defineEmits<{
        (e: 'save'): void
        (e: 'cancel'): void
    }>()

    const app = useNuxtApp()
    const {t} = useI18n()

    onMounted(async () => {
        if (props.editExcursionServiceId) await loadData()
        if (!props.editExcursionServiceId) {
            if (useCatalogue.value?.length) formData.value.categoryId = useCatalogue.value[0].id
            contract.value = supplierStore.fullContract
            const activeLangCode = app.$i18n.locale.value || 'en'
            formData.value.name[activeLangCode] = contract.value?.productVariant?.variantName
                ? getName(contract.value.productVariant.variantName)
                : `${t(`sharingType.${contract.value.sharingType}`)}, ${t(`transportationType.${contract.value.transportationType}`)}`
        }
        formData.value.accompanimentInfos = formData.value.accompanimentInfos.map(item =>
            Object.assign(item, {
                id: nanoid(),
                languageCode: item.languageCode ? item.languageCode : 0,
            })
        )
        iso2Codes.value = Array.from(new Set(runtimeStore.countries.map(({iso2Code}) => iso2Code))).sort((a, b) =>
            a.localeCompare(b)
        )
    })

    async function loadData(): Promise<void> {
        try {
            isLoading.value = true
            const rq = {id: props.editExcursionServiceId, locale: 'all'}
            const {services} = await app.$api.ownExcursionServices.get(rq)
            formData.value = services[0]
        } catch (e) {
            formData.value = initExcursionServiceFormData()
        } finally {
            isLoading.value = false
        }
    }

    const save = async (): Promise<void> => {
        try {
            isLoading.value = true
            const data = clone(formData.value)
            clean(data)
            if (data.accompanimentInfos) {
                data.accompanimentInfos = data.accompanimentInfos.map(({type, languageCode}) => ({
                    type,
                    languageCode: languageCode !== 0 ? languageCode : null,
                }))
            }
            if (props.editExcursionServiceId) {
                await app.$api.ownExcursionServices.put({
                    contractId: props.contractId,
                    ...data,
                })
            } else {
                await app.$api.ownExcursionServices.post({
                    contractId: props.contractId,
                    ...data,
                })
            }

            await supplierStore.loadProductServices()
            app.$toast.success(t('success_message.saved_successfully'))
            emit('save')
        } catch (e) {
            app.$toast.error(t('error_message.save_error'))
        } finally {
            isLoading.value = false
        }
    }

    const formatTime = (event, key): void => {
        const filteredValue = event.replace(/[^0-9:]/g, '')
        const formattedValue = filteredValue.replace(/(\d{2})(?=\d)/g, '$1:')
        formData.value[key] = formattedValue
    }

    const addRow = (): void => {
        const row = {languageCode: 0, type: ExcursionService.Other, id: nanoid()}
        formData.value.accompanimentInfos.push(row)
    }

    const removeRow = (id: string): void => {
        formData.value.accompanimentInfos = formData.value.accompanimentInfos.filter(item => id !== item.id)
    }

    const useExcursionService = computed(() => {
        const service: ExcursionService[] = [ExcursionService.Audio, ExcursionService.Guide, ExcursionService.Other]
        return service.map(value => ({value, title: t(`escortType.${value}`)}))
    })

    const useLanguages = computed(() => {
        const locales = Object.values(app.$conf.public.locales).map(lang => lang.toUpperCase())
        const allCodes = iso2Codes.value.filter(lang => !locales.includes(lang))
        const lang = [0, ...locales, ...allCodes]
        return lang.map(value => ({value, title: !value ? t('without_escort') : value}))
    })
</script>
